import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  LinearProgress
} from '@material-ui/core';
import { FaInfoCircle } from 'react-icons/fa';
import { ethers } from 'ethers';
import { 
  metamaskLogo, 
  futLogoIcon200,
  E3D_TOKEN_ADDRESS,
  E3D_NFT_MANAGER_ADDRESS,
  E3D_TOKEN_ABI,
  E3D_NFT_MANAGER_ABI
} from './config';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#121212',
    color: '#ffffff'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  appLogo: {
    height: 40,
    marginRight: theme.spacing(2)
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    color: '#ffffff'
  },
  stakeCard: {
    backgroundColor: '#1f2026',
    borderRadius: 16,
    maxWidth: 450,
    width: '100%',
    color: '#ffffff',
    border: '1px solid #4d79ff',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: '1px solid rgba(77, 121, 255, 0.4)'
  },
  cardContent: {
    padding: theme.spacing(2)
  },
  walletButton: {
    borderRadius: '20px',
    borderColor: 'white',
    borderWidth: '2px',
    borderStyle: 'solid',
    backgroundColor: '#333',
    color: '#fff',
    marginRight: '20px',
    height: '40px',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      backgroundColor: '#444'
    }
  },
  walletIcon: {
    height: '20px',
    marginRight: '8px'
  },
  tokenContainer: {
    backgroundColor: '#252525',
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  inputRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  amountInput: {
    '& .MuiInputBase-input': {
      color: '#ffffff',
      textAlign: 'right',
      fontSize: '24px',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: 'transparent'
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'transparent'
    }
  },
  tokenInfo: {
    display: 'flex',
    alignItems: 'center',
  },
  tokenIconContainer: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    backgroundColor: '#333',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  tokenIcon: {
    width: 24,
    height: 24,
    objectFit: 'contain'
  },
  tokenSymbol: {
    fontWeight: 'bold',
    color: '#ffffff'
  },
  stakeInfo: {
    marginTop: theme.spacing(2),
    backgroundColor: '#252525',
    borderRadius: 8,
    padding: theme.spacing(1),
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    '& .MuiTypography-body2': {
      color: '#cccccc'
    }
  },
  btnStake: {
    backgroundColor: '#4C6FFF',
    color: 'white',
    fontWeight: 'bold',
    borderRadius: 16,
    padding: '12px 0',
    marginTop: theme.spacing(2),
    width: '100%',
    '&:hover': {
      backgroundColor: '#3A5AE0',
    },
    '&.Mui-disabled': {
      backgroundColor: '#2A3A80',
      color: '#999'
    }
  },
  btnUnstake: {
    backgroundColor: '#D32F2F',
    color: 'white',
    fontWeight: 'bold',
    borderRadius: 16,
    padding: '12px 0',
    marginTop: theme.spacing(1),
    width: '100%',
    '&:hover': {
      backgroundColor: '#B71C1C',
    },
    '&.Mui-disabled': {
      backgroundColor: '#7F1818',
      color: '#999'
    }
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: '#ffffff'
  },
  settingsButton: {
    color: '#cccccc',
  },
  benefitContainer: {
    backgroundColor: '#292933',
    borderRadius: 15,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: '1px solid #4d79ff',
  },
  benefitTitle: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: '#4C6FFF'
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    '& svg': {
      marginRight: theme.spacing(1),
      color: '#4C6FFF'
    }
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    padding: theme.spacing(2),
  }
}));

const StakePage = ({ 
  open = null, 
  onClose = null,
  provider: externalProvider = null,
  walletAddress: externalWalletAddress = null,
  isDialog = open !== null
}) => {
  const classes = useStyles();
  const navigate = useNavigate();

  // State variables
  const [provider, setProvider] = useState(externalProvider);
  const [walletAddress, setWalletAddress] = useState(externalWalletAddress);
  // ETH balance no longer needed as we focus only on E3D tokens
  const [tokenBalance, setTokenBalance] = useState('0.00');
  const [stakedAmount, setStakedAmount] = useState('0.00');
  const [inputAmount, setInputAmount] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [transactionSuccess, setTransactionSuccess] = useState(false);
  const [transactionType, setTransactionType] = useState('');
  const [stakingRate, setStakingRate] = useState(1); // Initialize with contract's default value of 1%

  // Connect wallet function
  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        setIsLoading(true);
        setErrorMessage('');
        
        console.log("Requesting wallet connection with account selection");
        
        // Always force the account selection dialog to appear
        await window.ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        });
        
        // After permission is granted, get the selected account
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        
        console.log("Accounts received:", accounts);
        if (accounts.length > 0) {
          setWalletAddress(accounts[0]);
          // Get updated account info
          const web3Provider = new ethers.providers.Web3Provider(window.ethereum);
          setProvider(web3Provider);
          console.log("Wallet connected successfully");
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to connect wallet:", error);
        setErrorMessage("Failed to connect wallet: " + (error.message || "Unknown error"));
        setIsLoading(false);
      }
    } else {
      setErrorMessage('MetaMask is not installed. Please consider installing it: https://metamask.io/download.html');
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    console.log("Disconnecting wallet");
    setWalletAddress(null);
    setProvider(null);
    // ETH balance tracking removed
    setTokenBalance('0.00');
    setStakedAmount('0.00');
    setInputAmount('');
    setTransactionSuccess(false);
    setErrorMessage('');
  };

  // Update balances when wallet or provider changes
  useEffect(() => {
    const fetchBalances = async () => {
      if (provider && walletAddress) {
        try {
          // We no longer need to fetch ETH balance as we're focusing only on E3D tokens
          
          // Get E3D token balance
          const e3dContract = new ethers.Contract(E3D_TOKEN_ADDRESS, E3D_TOKEN_ABI, provider);
          const tokenBalance = await e3dContract.balanceOf(walletAddress);
          const decimals = await e3dContract.decimals();
          const formattedTokenBalance = parseFloat(ethers.utils.formatUnits(tokenBalance, decimals)).toFixed(2);
          setTokenBalance(formattedTokenBalance);
          
          // Get staked E3D amount
          const nftManagerContract = new ethers.Contract(E3D_NFT_MANAGER_ADDRESS, E3D_NFT_MANAGER_ABI, provider);
          const stakedE3D = await nftManagerContract.stakedE3D(walletAddress);
          const formattedStakedAmount = parseFloat(ethers.utils.formatUnits(stakedE3D, decimals)).toFixed(2);
          setStakedAmount(formattedStakedAmount);
          
          // Get staking reward rate
          const rewardRateStaking = await nftManagerContract.rewardRateStaking();
          setStakingRate(parseInt(rewardRateStaking.toString()));
        } catch (error) {
          console.error("Error fetching balances:", error);
          setErrorMessage("Error fetching balances: " + (error.message || "Unknown error"));
        }
      }
    };
    
    fetchBalances();
  }, [provider, walletAddress]);

  // Helper to shorten wallet address for display
  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Stake E3D tokens
  const stakeTokens = async () => {
    if (!provider || !walletAddress) {
      setErrorMessage("Please connect your wallet");
      return;
    }
    
    if (parseFloat(inputAmount) <= 0 || isNaN(parseFloat(inputAmount))) {
      setErrorMessage("Please enter a valid amount");
      return;
    }
    
    if (parseFloat(inputAmount) > parseFloat(tokenBalance)) {
      setErrorMessage("Insufficient E3D balance");
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage('');
      setTransactionSuccess(false);
      
      // Use window.ethereum directly just like MintNFTDialog does
      if (!window.ethereum) throw new Error("MetaMask is not installed.");
      await window.ethereum.request({ method: "eth_requestAccounts" });

      // Check if we're on Ethereum mainnet
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      console.log("Current chainId:", chainId);
      if (chainId !== '0x1') { // 0x1 is Ethereum mainnet
        console.log("Not on Ethereum mainnet, prompting to switch");
        try {
          // Ask user to switch to Ethereum mainnet
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x1' }], // 0x1 is Ethereum mainnet
          });
          console.log("Switched to Ethereum mainnet");
        } catch (switchError) {
          // This error code indicates that the chain has not been added to MetaMask
          console.error("Failed to switch network:", switchError);
          setErrorMessage("Please switch to Ethereum mainnet in your wallet and try again");
          setIsLoading(false);
          return;
        }
      }
      
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      
      // Use hard-coded addresses rather than config imports (just to match MintNFTDialog exactly)
      const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";
      const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
      
      const e3dContract = new ethers.Contract(E3DToken_ADDRESS, E3D_TOKEN_ABI, signer);
      const nftManagerContract = new ethers.Contract(NFTManager_ADDRESS, E3D_NFT_MANAGER_ABI, signer);
      
      // Amount to stake in wei
      const amountToStake = ethers.utils.parseUnits(inputAmount, 18);
      
      // Check if the contract exists first by getting token symbol
      try {
        const symbol = await e3dContract.symbol();
        console.log("Token symbol:", symbol);
        if (symbol !== 'E3D') {
          throw new Error("E3D token contract not found or incorrect address");
        }
      } catch (contractError) {
        console.error("Error checking E3D token contract:", contractError);
        setErrorMessage("Error: Cannot find E3D token contract. Please make sure you're on Ethereum mainnet.");
        setIsLoading(false);
        return;
      }
      
      // Try reading staked amount to verify NFT Manager contract
      try {
        const currentStaked = await nftManagerContract.stakedE3D(walletAddress);
        console.log("Current staked amount:", ethers.utils.formatUnits(currentStaked, 18));
      } catch (contractError) {
        console.error("Error checking NFT Manager contract:", contractError);
        setErrorMessage("Error: Cannot find E3D NFT Manager contract. Please make sure you're on Ethereum mainnet.");
        setIsLoading(false);
        return;
      }

      // Exactly as in MintNFTDialog.js - just approve without checking allowance first
      console.log("Approving tokens...");
      setErrorMessage("Approving tokens... Please confirm the transaction in your wallet");
      
      try {
        // Set gas limit explicitly to help with transaction execution
        const gasEstimate = await e3dContract.estimateGas.approve(NFTManager_ADDRESS, amountToStake);
        console.log("Gas estimate for approval:", gasEstimate.toString());
        
        const approvalTx = await e3dContract.approve(
          NFTManager_ADDRESS, 
          amountToStake,
          { gasLimit: gasEstimate.mul(120).div(100) } // Add 20% buffer to gas estimate
        );
        console.log("Approval TX hash:", approvalTx.hash);
        await approvalTx.wait();
      } catch (approvalError) {
        console.error("Approval transaction failed:", approvalError);
        setErrorMessage("Failed to approve tokens: " + (approvalError.message || "Unknown error"));
        setIsLoading(false);
        return;
      }
      console.log("Token approval successful");
      
      // Execute the stake transaction
      console.log(`Staking ${inputAmount} E3D tokens`);
      setErrorMessage("Now staking... Please confirm the transaction in your wallet");
      
      try {
        // Set gas limit explicitly to help with transaction execution
        const gasEstimate = await nftManagerContract.estimateGas.stake(amountToStake);
        console.log("Gas estimate for staking:", gasEstimate.toString());
        
        const stakeTx = await nftManagerContract.stake(
          amountToStake,
          { gasLimit: gasEstimate.mul(120).div(100) } // Add 20% buffer to gas estimate
        );
        console.log("Stake transaction hash:", stakeTx.hash);
        setErrorMessage("Staking transaction submitted. Waiting for confirmation...");
        
        const stakeReceipt = await stakeTx.wait();
      } catch (stakeError) {
        console.error("Staking transaction failed:", stakeError);
        setErrorMessage("Failed to stake tokens: " + (stakeError.message || "Unknown error"));
        setIsLoading(false);
        return;
      }
      console.log("Staking successful!");
      
      // Update balances
      const tokenBalance = await e3dContract.balanceOf(walletAddress);
      const formattedTokenBalance = parseFloat(ethers.utils.formatUnits(tokenBalance, 18)).toFixed(2);
      setTokenBalance(formattedTokenBalance);
      
      const stakedE3D = await nftManagerContract.stakedE3D(walletAddress);
      const formattedStakedAmount = parseFloat(ethers.utils.formatUnits(stakedE3D, 18)).toFixed(2);
      setStakedAmount(formattedStakedAmount);
      
      setTransactionSuccess(true);
      setTransactionType('stake');
      setInputAmount('');
      setErrorMessage('');
    } catch (error) {
      console.error("Error staking tokens:", error);
      
      // Handle user rejection without showing error
      if (
        error.code === 4001 || // MetaMask error code for user rejection
        error.code === "ACTION_REJECTED" ||
        (error.code && error.code.toString().includes("ACTION_REJECTED")) ||
        (error.message && error.message.includes("user rejected")) ||
        (error.message && error.message.includes("User denied")) ||
        (error.message && error.message.includes("User rejected"))
      ) {
        console.log('User rejected the transaction - no error displayed');
      } else {
        setErrorMessage("Error staking tokens: " + (error.message || "Unknown error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Unstake E3D tokens
  const unstakeTokens = async () => {
    if (!provider || !walletAddress) {
      setErrorMessage("Please connect your wallet");
      return;
    }
    
    if (parseFloat(inputAmount) <= 0 || isNaN(parseFloat(inputAmount))) {
      setErrorMessage("Please enter a valid amount");
      return;
    }
    
    if (parseFloat(inputAmount) > parseFloat(stakedAmount)) {
      setErrorMessage("Insufficient staked E3D");
      return;
    }
    
    try {
      setIsLoading(true);
      setErrorMessage('');
      setTransactionSuccess(false);
      
      // Use window.ethereum directly just like MintNFTDialog does
      if (!window.ethereum) throw new Error("MetaMask is not installed.");
      await window.ethereum.request({ method: "eth_requestAccounts" });

      // Check if we're on Ethereum mainnet
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      console.log("Current chainId:", chainId);
      if (chainId !== '0x1') { // 0x1 is Ethereum mainnet
        console.log("Not on Ethereum mainnet, prompting to switch");
        try {
          // Ask user to switch to Ethereum mainnet
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x1' }], // 0x1 is Ethereum mainnet
          });
          console.log("Switched to Ethereum mainnet");
        } catch (switchError) {
          // This error code indicates that the chain has not been added to MetaMask
          console.error("Failed to switch network:", switchError);
          setErrorMessage("Please switch to Ethereum mainnet in your wallet and try again");
          setIsLoading(false);
          return;
        }
      }
      
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      
      // Use hard-coded addresses rather than config imports (just to match MintNFTDialog exactly)
      const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";
      const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
      
      const e3dContract = new ethers.Contract(E3DToken_ADDRESS, E3D_TOKEN_ABI, signer);
      const nftManagerContract = new ethers.Contract(NFTManager_ADDRESS, E3D_NFT_MANAGER_ABI, signer);
      
      // Amount to unstake in wei
      const amountToUnstake = ethers.utils.parseUnits(inputAmount, 18);
      
      // Execute the unstake transaction
      console.log(`Unstaking ${inputAmount} E3D tokens`);
      setErrorMessage("Unstaking... Please confirm the transaction in your wallet");
      
      try {
        // Set gas limit explicitly to help with transaction execution
        const gasEstimate = await nftManagerContract.estimateGas.withdrawStake(amountToUnstake);
        console.log("Gas estimate for unstaking:", gasEstimate.toString());
        
        const unstakeTx = await nftManagerContract.withdrawStake(
          amountToUnstake,
          { gasLimit: gasEstimate.mul(120).div(100) } // Add 20% buffer to gas estimate
        );
        console.log("Unstake transaction hash:", unstakeTx.hash);
        setErrorMessage("Unstaking transaction submitted. Waiting for confirmation...");
        
        const unstakeReceipt = await unstakeTx.wait();
      } catch (unstakeError) {
        console.error("Unstaking transaction failed:", unstakeError);
        setErrorMessage("Failed to unstake tokens: " + (unstakeError.message || "Unknown error"));
        setIsLoading(false);
        return;
      }
      console.log("Unstaking successful!");
      
      // Update balances
      const tokenBalance = await e3dContract.balanceOf(walletAddress);
      const formattedTokenBalance = parseFloat(ethers.utils.formatUnits(tokenBalance, 18)).toFixed(2);
      setTokenBalance(formattedTokenBalance);
      
      const stakedE3D = await nftManagerContract.stakedE3D(walletAddress);
      const formattedStakedAmount = parseFloat(ethers.utils.formatUnits(stakedE3D, 18)).toFixed(2);
      setStakedAmount(formattedStakedAmount);
      
      setTransactionSuccess(true);
      setTransactionType('unstake');
      setInputAmount('');
      setErrorMessage('');
    } catch (error) {
      console.error("Error unstaking tokens:", error);
      
      // Handle user rejection without showing error
      if (
        error.code === 4001 || // MetaMask error code for user rejection
        error.code === "ACTION_REJECTED" ||
        (error.code && error.code.toString().includes("ACTION_REJECTED")) ||
        (error.message && error.message.includes("user rejected")) ||
        (error.message && error.message.includes("User denied")) ||
        (error.message && error.message.includes("User rejected"))
      ) {
        console.log('User rejected the transaction - no error displayed');
      } else {
        setErrorMessage("Error unstaking tokens: " + (error.message || "Unknown error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate staking benefits based on amount
  const calculateBenefits = (amount) => {
    const parsedAmount = parseFloat(amount);
    
    let mintDiscount = 0;
    let royaltyRate = 10; // Default 10%
    
    if (parsedAmount >= 1000) {
      mintDiscount = 30;
      royaltyRate = 5;
    } else if (parsedAmount >= 500) {
      mintDiscount = 20;
      royaltyRate = 7;
    } else if (parsedAmount >= 100) {
      mintDiscount = 10;
      royaltyRate = 10;
    }
    
    return { mintDiscount, royaltyRate };
  };

  // Current benefits based on staked amount
  const currentBenefits = calculateBenefits(stakedAmount);
  
  // Add global style to remove white borders
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    // Define CSS to remove white borders
    styleElement.innerHTML = `
      body, html {
        margin: 0 !important;
        padding: 0 !important;
        overflow: auto !important;
        background-color: #121212 !important;
        border: none !important;
      }
    `;
    // Append style to document head
    document.head.appendChild(styleElement);
    
    // Cleanup function
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);
  
  // Content of the stake page/dialog
  const stakeContent = (
    <div className={classes.root} style={{ padding: '0', display: 'flex', flexDirection: 'column' }}>

      {/* Wallet Button centered with less vertical space */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
        {!walletAddress ? (
          <Button 
            className={classes.walletButton}
            onClick={connectWallet}
            disabled={isLoading}
          >
            <img src={metamaskLogo} alt="MetaMask" className={classes.walletIcon} />
            {isLoading ? 'Connecting...' : 'Connect Wallet'}
          </Button>
        ) : (
          <Button 
            className={classes.walletButton}
            onClick={disconnectWallet}
          >
            <img src={metamaskLogo} alt="MetaMask" className={classes.walletIcon} />
            {shortenAddress(walletAddress)}
          </Button>
        )}
      </div>

      <Card className={classes.stakeCard}>
        <CardContent className={classes.cardContent}>
          {/* Token Input */}
          <div className={classes.tokenContainer}>
            <div className={classes.inputRow}>
              <div className={classes.tokenInfo}>
                <div className={classes.tokenIconContainer}>
                  <img 
                    src={futLogoIcon200} 
                    alt="E3D" 
                    className={classes.tokenIcon} 
                  />
                </div>
                <Typography variant="h6" className={classes.tokenSymbol}>
                  E3D
                </Typography>
              </div>
              <TextField
                className={classes.amountInput}
                value={inputAmount}
                onChange={(e) => setInputAmount(e.target.value)}
                placeholder="0.00"
                type="number"
                InputProps={{
                  inputProps: { 
                    min: "0",
                    step: "1"
                  }
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <Typography variant="body2" style={{ color: '#ffffff' }}>
                Wallet Balance: {tokenBalance} E3D
              </Typography>
              <Typography 
                variant="body2" 
                style={{ color: '#4C6FFF', cursor: 'pointer' }}
                onClick={() => setInputAmount(tokenBalance)}
              >
                Max
              </Typography>
            </div>
          </div>
          
          {/* Staked Amount */}
          <div className={classes.stakeInfo}>
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Currently Staked
                <Tooltip title="Amount of E3D tokens currently staked">
                  <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                    <FaInfoCircle size={12} />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2">
                {stakedAmount} E3D
              </Typography>
            </div>
            
            {stakingRate > 0 && (
              <div className={classes.infoRow}>
                <Typography variant="body2">
                  Staking APR
                  <Tooltip title="Annual Percentage Rate for staking rewards">
                    <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                      <FaInfoCircle size={12} />
                    </IconButton>
                  </Tooltip>
                </Typography>
                <Typography variant="body2">
                  {stakingRate}%
                </Typography>
              </div>
            )}
            
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Minting Discount
                <Tooltip title="Discount on NFT minting fees">
                  <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                    <FaInfoCircle size={12} />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2">
                {currentBenefits.mintDiscount}%
              </Typography>
            </div>
            
            <div className={classes.infoRow}>
              <Typography variant="body2">
                Royalty Rate
                <Tooltip title="Royalty rate applied to your NFTs">
                  <IconButton size="small" style={{ color: '#999', padding: '2px' }}>
                    <FaInfoCircle size={12} />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Typography variant="body2">
                {currentBenefits.royaltyRate}%
              </Typography>
            </div>
          </div>
          
          {/* Error Message */}
          {errorMessage && (
            <Typography 
              variant="body2" 
              style={{ color: '#f44336', marginTop: 8, textAlign: 'center' }}
            >
              {errorMessage}
            </Typography>
          )}
          
          {/* Success Message */}
          {transactionSuccess && (
            <Typography 
              variant="body2" 
              style={{ color: '#4caf50', marginTop: 8, textAlign: 'center' }}
            >
              {transactionType === 'stake' 
                ? 'Tokens staked successfully!' 
                : 'Tokens unstaked successfully!'}
            </Typography>
          )}
          
          {/* Loading Indicator */}
          {isLoading && (
            <div style={{ marginTop: 16 }}>
              <LinearProgress style={{ backgroundColor: '#333', color: '#4C6FFF' }} />
              <Typography 
                variant="body2" 
                style={{ color: '#ffffff', marginTop: 8, textAlign: 'center' }}
              >
                Processing transaction...
              </Typography>
            </div>
          )}
          
          {/* Action Buttons */}
          <Button
            className={classes.btnStake}
            onClick={stakeTokens}
            disabled={!walletAddress || isLoading || !inputAmount || parseFloat(inputAmount) <= 0 || parseFloat(inputAmount) > parseFloat(tokenBalance)}
          >
            Stake E3D
          </Button>
          
          <Button
            className={classes.btnUnstake}
            onClick={unstakeTokens}
            disabled={!walletAddress || isLoading || parseFloat(stakedAmount) <= 0 || (inputAmount && (parseFloat(inputAmount) <= 0 || parseFloat(inputAmount) > parseFloat(stakedAmount)))}
          >
            Unstake E3D
          </Button>
          
          {/* Benefits Container */}
          <div className={classes.benefitContainer}>
            <Typography variant="h6" className={classes.benefitTitle}>
              Staking Benefits
            </Typography>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>100+ E3D:</strong> 10% discount on NFT minting
              </Typography>
            </div>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>500+ E3D:</strong> 20% discount on NFT minting, 7% royalty rate (vs 10%)
              </Typography>
            </div>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>1000+ E3D:</strong> 30% discount on NFT minting, 5% royalty rate
              </Typography>
            </div>
            
            <div className={classes.benefitItem}>
              <FaInfoCircle />
              <Typography variant="body2">
                <strong>Any amount:</strong> {stakingRate}% APR staking rewards
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Debug what's happening with the dialog mode
  console.log('StakePage render state:', { open, isDialog, walletAddress });
  
  // Check if walletAddress from props is null or undefined but we have a provider
  useEffect(() => {
    if ((!walletAddress || walletAddress === null) && provider) {
      const getAddress = async () => {
        try {
          const accounts = await provider.listAccounts();
          if (accounts && accounts.length > 0) {
            setWalletAddress(accounts[0]);
            console.log('StakePage: Set wallet address from provider:', accounts[0]);
          }
        } catch (error) {
          console.error('StakePage: Error getting wallet address from provider', error);
        }
      };
      getAddress();
    }
  }, [provider, walletAddress]);

  // Don't add any global style overrides that might break the dialog structure

  // Conditional rendering based on whether it's used as a dialog or standalone page
  return isDialog ? (
    <Dialog
      open={open === null ? false : open}
      onClose={onClose}
      maxWidth="sm"
      scroll="body"
      fullWidth={false}
      style={{ zIndex: 10000 }}
      PaperProps={{
        style: {
          border: '1px solid #4d79ff',
          borderRadius: '15px',
          maxWidth: '450px',
          margin: '0 auto',
          boxSizing: 'border-box',
          backgroundColor: '#1f2026',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch'
        }
      }}
    >
      <DialogTitle style={{ backgroundColor: '#1f2026', color: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            E3D Token Staking
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ backgroundColor: '#1f2026', padding: 0, height: 'auto', overflow: 'visible' }}>
        {stakeContent}
      </DialogContent>
    </Dialog>
  ) : (
    // Standalone page view with more padding
    <div style={{ padding: 20 }}>
      {stakeContent}
    </div>
  );
};

export default StakePage;
